import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Award, Lock } from "lucide-react";

export default function TitlesPanel({ ch, setCh }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setData(await api.titles());
  useEffect(() => { refresh(); }, [ch.level, ch.stats.charisma, ch.arena_wins]);

  const equip = async (id) => {
    setBusy(true);
    try {
      const updated = await api.titleEquip(id);
      setCh(updated);
      await refresh();
      toast(id ? t("titles.displayed") : t("titles.cleared"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  if (!data) return null;

  const earnedCount = data.titles.filter(x => x.earned).length;

  return (
    <div className="panel p-3 space-y-2" data-testid="titles-panel">
      <div className="flex items-center justify-between">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
          <Award className="w-4 h-4 text-[#B89C3A]" strokeWidth={1.5} /> {t("titles.title")}
        </div>
        <span className="font-mono text-[10px] text-[#8c8c8c]">
          {earnedCount}/{data.titles.length}
        </span>
      </div>
      {data.active && (
        <button
          data-testid="title-clear"
          onClick={() => equip(null)}
          disabled={busy}
          className="w-full py-1 border border-[#333] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase"
        >
          {t("titles.clear")}
        </button>
      )}
      <div className="space-y-1">
        {data.titles.map(x => {
          const isActive = data.active === x.id;
          return (
            <div key={x.id} data-testid={`title-${x.id}`}
              className={`panel-inner p-2 flex items-center justify-between border-l-4 ${
                isActive ? "border-l-[#E55A00]" : x.earned ? "border-l-[#B89C3A]" : "border-l-[#333]"
              }`}>
              <div className="min-w-0">
                <div className={`font-heading text-xs tracking-wider ${x.earned ? "text-[#E5C34A]" : "text-[#666]"}`}>
                  {x.name}
                </div>
                <div className="text-[10px] italic text-[#8c8c8c]">{x.hint}</div>
              </div>
              {x.earned ? (
                isActive ? (
                  <span className="px-2 py-0.5 text-[10px] font-heading tracking-widest uppercase text-[#E55A00] border border-[#E55A00]">
                    {t("titles.worn")}
                  </span>
                ) : (
                  <button data-testid={`title-equip-${x.id}`}
                    onClick={() => equip(x.id)}
                    disabled={busy}
                    className="px-2 py-0.5 text-[10px] font-heading tracking-widest uppercase border border-[#333] text-[#8c8c8c] hover:border-[#E55A00] hover:text-[#E55A00]">
                    {t("titles.wear")}
                  </button>
                )
              ) : (
                <Lock className="w-3 h-3 text-[#666]" strokeWidth={1.5} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
